import { IArticle, IPlannedOrderedArticleLine } from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { groupBy } from 'lodash';
import { ArticleDB } from 'src/app/common/repository/databases';
import type {
  PlannedOrderSummaryLine,
  // tslint:disable-next-line:max-line-length
} from 'src/app/patient/components/patient-detail/sections/dashboard/tasks/planned-order-detail/planned-order-detail.component';
import { CrossTherapyArticleLineViewModel } from '../../../business/cross-therapy/cross-therapy-article-line-view-model';
import { QueryService } from '../../services/query/query.service';
import type { SummaryArticleLine } from './document-edit/nodes/cross-therapy-summary/swodoc-node-cross-therapy-summary';

@Injectable()
export class GroupArticleLinesService {
  public groupArticles(crossTherapyArticleLines: CrossTherapyArticleLineViewModel[]): SummaryArticleLine[] {
    const articleLines = [];
    const groupedArticleLines = groupBy(
      crossTherapyArticleLines.filter(item => item.quantity > 0),
      item => `${item.articleId} ${item.therapyId} ${item.therapyTypeId}`
    );
    Object.keys(groupedArticleLines).forEach(key => {
      const firstArticleLine = groupedArticleLines[key][0] as any;
      const summaryArticleLine: SummaryArticleLine = {
        articleId: firstArticleLine.articleId,
        articleName: firstArticleLine.articleName,
        therapyId: firstArticleLine.therapyId,
        therapyTypeId: firstArticleLine.therapyTypeId,
        articleDescription: firstArticleLine.articleDescription,
        articleCustomerNumber: firstArticleLine.articleCustomerNumber,
        totalAmount: groupedArticleLines[key]
          .map(item => item.quantity * item.packagingUnitQuantity)
          .reduce((a, b) => a + b, 0),
        articles: groupedArticleLines[key].map(item => {
          return {
            resmedTenant: item.resmedTenant,
            batchNumber: item.batchNumber,
            serialNumber: item.serialNumber,
            storageLocation: item.storageLocation,
            operatingHours: item.operatingHours,
            unit: item.unit,
            quantity: item.quantity,
            packagingUnitQuantity: item.packagingUnitQuantity,
            totalPrice: item.totalPrice,
            ordered: item.ordered,
          };
        }),
      };
      articleLines.push(summaryArticleLine);
    });
    return articleLines;
  }

  public async plannedOrderArticleLinesToSummaryArticleLine(
    articleLines: IPlannedOrderedArticleLine[],
    queryService: QueryService
  ): Promise<PlannedOrderSummaryLine[]> {
    const groupedArticleLines = groupBy(
      articleLines.filter(item => item.quantity > 0),
      item => `${item.articleId} ${item.therapyId} ${item.therapyTypeId}`
    );

    const newArticleLines = [];
    for (const key of Object.keys(groupedArticleLines)) {
      const firstArticleLine = groupedArticleLines[key][0] as any;
      const article = await queryService.get<IArticle>(firstArticleLine.articleId, ArticleDB);
      const summaryArticleLine: PlannedOrderSummaryLine = {
        articleId: firstArticleLine.articleId,
        articleName: firstArticleLine.articleName,
        therapyId: firstArticleLine.therapyId,
        therapyTypeId: firstArticleLine.therapyTypeId,
        articleCustomerNumber: firstArticleLine.articleCustomerNumber,
        totalAmount: groupedArticleLines[key]
          .map(
            item =>
              item.quantity *
              (article.packagingUnits.find(packagingUnit => packagingUnit.unit === item.unit).quantity
                ? article.packagingUnits.find(packagingUnit => packagingUnit.unit === item.unit).quantity
                : 1)
          )
          .reduce((a, b) => a + b, 0),
        articles: groupedArticleLines[key].map(item => {
          return {
            name: article.name,
            description: article.description,
            batchNumber: item.batchNumber,
            serialNumber: item.serialNumber,
            storageLocation: item.storageLocation,
            deliveredQuantity: item.deliveredQuantity,
            bookingCode: item.bookingCode,
            validTo: item.validTo,
            salesLineCase: item.salesLineCase,
            salesLinePeriodCase: item.salesLinePeriodCase,
            unit: item.unit,
            quantity: item.quantity,
            packagingUnitQuantity: article.packagingUnits.find(packagingUnit => packagingUnit.unit === item.unit)
              .quantity
              ? article.packagingUnits.find(packagingUnit => packagingUnit.unit === item.unit).quantity
              : 1,
            isPrivateSale: item.isPrivateSale,
          };
        }),
      };
      newArticleLines.push(summaryArticleLine);
    }
    return newArticleLines;
  }
}
